import { toaster } from '@/components/ui/toaster';
import { useRevokeDriveAccess, useShareDrive } from '@/hooks/actions/use-drive-share-actions';
import { useDrivePermissionsQuery } from '@/hooks/queries/use-drive-permissions-query';
import { useDriveStatusQuery } from '@/hooks/queries/use-drive-status-query';
import { useTeamMembersQuery } from '@/hooks/queries/use-team-members-query';
import type { GoogleDrivePermission } from '@fluenceur/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DriveMemberBadge } from './DriveMemberRow';
import { buildSharedEmailSet, parseExternalEmails } from './drive-sharing-helpers';

const I18N_PREFIX = 'integrations.googleDrive.sharing';

/**
 * All Drive manage-access state, data, and side effects. DriveManageAccessModal
 * is a thin composition shell on top of this hook. Extracted so the heavy
 * logic is testable in isolation and each rendered section stays small and
 * focused on presentation only.
 */
export const useDriveManageAccess = () => {
  const { t } = useTranslation();

  const { data: status } = useDriveStatusQuery(true);
  const {
    data: permissions = [],
    isLoading: isLoadingPermissions,
    isError: isPermissionsError,
  } = useDrivePermissionsQuery(true);
  const { data: teamMembers = [], isLoading: isLoadingTeam } = useTeamMembersQuery();

  const shareMutation = useShareDrive();
  const revokeMutation = useRevokeDriveAccess();

  const [pendingRevoke, setPendingRevoke] = useState<GoogleDrivePermission | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const alreadySharedEmails = useMemo(() => buildSharedEmailSet(permissions), [permissions]);
  const visiblePermissions = useMemo(() => permissions.filter((p) => !p.deleted), [permissions]);

  const teamEmails = useMemo(() => {
    const set = new Set<string>();
    teamMembers.forEach((m) => set.add(m.email.toLowerCase()));
    return set;
  }, [teamMembers]);

  const badgeFor = (permission: GoogleDrivePermission): DriveMemberBadge => {
    if (permission.isOwner) return 'owner';
    if (permission.type === 'domain') return 'domain';
    if (permission.emailAddress && teamEmails.has(permission.emailAddress.toLowerCase())) {
      return 'teammate';
    }
    return 'external';
  };

  const workspaceDomain = status?.workspaceDomain ?? null;

  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [shareWithDomain, setShareWithDomain] = useState(false);
  const [externalRaw, setExternalRaw] = useState('');

  // Seed "all teammates pre-checked" exactly once, when team + permissions first
  // populate. Without the ref, a background refetch would clobber in-progress
  // user edits. The admin keeps any manual toggles after that.
  const hasSeededSelection = useRef(false);
  useEffect(() => {
    if (hasSeededSelection.current) return;
    if (teamMembers.length === 0) return;
    hasSeededSelection.current = true;
    const initial = new Set<string>();
    for (const member of teamMembers) {
      if (!alreadySharedEmails.has(member.email.toLowerCase())) {
        initial.add(member.id);
      }
    }
    setSelectedMemberIds(initial);
  }, [teamMembers, alreadySharedEmails]);

  const toggleMember = (memberId: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const { valid: validExternal, invalid: invalidExternal } = parseExternalEmails(externalRaw);
  const validExternalNew = validExternal.filter((e) => !alreadySharedEmails.has(e));

  // Strip any selected teammate whose email already has access on the folder.
  // Needed because the seed effect fires before `permissions` finishes loading:
  // the first pass adds every teammate to the set, and later refetches can't
  // re-run the seed (hasSeededSelection ref is latched). Without this filter,
  // already-shared teammates would double-count in totalRecipients even though
  // Google would dedupe them server-side as `already_shared`.
  const selectedMemberEmails = teamMembers
    .filter((m) => selectedMemberIds.has(m.id))
    .map((m) => m.email.toLowerCase())
    .filter((email) => !alreadySharedEmails.has(email));
  const selectedEmails = Array.from(new Set([...selectedMemberEmails, ...validExternalNew]));

  const totalRecipients = selectedEmails.length + (shareWithDomain ? 1 : 0);
  const canSubmit = totalRecipients > 0 && invalidExternal.length === 0;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const results = await shareMutation.mutateAsync({
        emails: selectedEmails.length > 0 ? selectedEmails : undefined,
        domain: shareWithDomain && workspaceDomain ? workspaceDomain : undefined,
      });

      const total = results.length;
      const ok = results.filter(
        (r) => r.status === 'shared' || r.status === 'already_shared'
      ).length;
      const failed = total - ok;

      if (failed === 0) {
        toaster.create({
          description: t(`${I18N_PREFIX}.toast.shared`, { count: ok }),
          type: 'success',
        });
        setShareWithDomain(false);
        setExternalRaw('');
        hasSeededSelection.current = false;
      } else {
        toaster.create({
          description: t(`${I18N_PREFIX}.toast.partialShare`, { ok, total, failed }),
          type: 'error',
        });
      }
    } catch {
      toaster.create({
        description: t(`${I18N_PREFIX}.errors.shareFailed`),
        type: 'error',
      });
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    const id = pendingRevoke.id;
    setPendingRevoke(null);
    setRevokingId(id);
    try {
      await revokeMutation.mutateAsync(id);
      toaster.create({
        description: t(`${I18N_PREFIX}.toast.revoked`),
        type: 'success',
      });
    } catch {
      toaster.create({
        description: t(`${I18N_PREFIX}.errors.shareFailed`),
        type: 'error',
      });
    } finally {
      setRevokingId(null);
    }
  };

  return {
    // Current access
    permissions: visiblePermissions,
    badgeFor,
    isLoadingPermissions,
    isPermissionsError,
    revokingId,
    pendingRevoke,
    requestRevoke: setPendingRevoke,
    cancelRevoke: () => setPendingRevoke(null),
    confirmRevoke,
    revokeInFlight: revokeMutation.isPending,

    // Invite
    workspaceDomain,
    teamMembers,
    isLoadingTeam,
    alreadySharedEmails,
    selectedMemberIds,
    toggleMember,
    shareWithDomain,
    setShareWithDomain,
    externalRaw,
    setExternalRaw,
    invalidExternal,
    totalRecipients,
    canSubmit,
    submit,
    submitInFlight: shareMutation.isPending,
  };
};
